// The requisition detail (DS §4 B2 row; DC-65 "tabs and content left, a right rail"): `#requisitions/R-####` and its
// Requisition timeline tab, in the Mission Control design. Header: the title with the requisition's status words
// (the creation chain "Request Approved -> Req Creation Pending -> Created -> Posted.", the administrative state, the
// recruiter-assignment chain) and its people; a null Recruiter shows the assignment word, never "Unassigned" (B1 D12).
// Under it the StateBanner (row 83) for an administrative state and the MilestoneTimeline (row 38). Tabs are route links:
// Requisition (this page; DC-65's *Overview* is a design word, not his) · Intake & Panel and Candidates (the V1
// sections, until B4 and B3 move them) · Requisition timeline. The right rail carries the record's fields, its Clocks
// (row 84) and the Next action rows addressed to the viewer on this requisition. F-186: the header's Actions menu
// carries Record what already happened, Change openings and Review openings changes (and, on the Intake & Panel tab,
// Print the intake form), each behind the gate its V1 button had; their V1 dialogs open from the URL (`?action=record`,
// `?action=openings`) and still render through the shell's V1 slot (shell/island.tsx), outside the view's `.mc`
// island. The V1 controls with no place in the menu yet (Request access, Resume and the filled close) keep their V1
// row there, drawn only when one of them shows. Hold, cancel and reopen are asked for in the one request dialog and
// decided on the
// *HRBP approval* card (StateChange.tsx, S10). The other V1 sections (packets, debriefs, offer, concerns, access,
// activity) keep the V1 page at their own routes.
// Hosted by MissionShell: views/b2/register.ts registers it through S12's registerView, so it draws no McRoot of its own.
// The rail also carries *Req health* (`health.open`) and *Search Readiness* (`readiness.open`), for a viewer who may
// read them (SpineCards.tsx).
//
// Mount points for other lanes (B3 FU-TAB, B8 S12, B9 S11/S12, T-12), called at import from the lane's own register.ts:
//
//   registerRequisitionTab(def: RequisitionTabDef): void
//     def = { key, label, lane, component, visible?, order?, deep? }  (views/requisition/slots.ts)
//     Adds the tab `#requisitions/R-####/<key>` (key: lowercase letters, digits and hyphens) and registers its route
//     with registerView, so the lane registers no route of its own for it; `deep` also claims `<key>/…`. The tab shows
//     where `visible(can, r)` is true (a manifest tool, never a role); `component` gets { r, timeZone, rest } and draws
//     the main column beside the record's rail. A key equal to "intake" or "candidates" replaces the V1 link of that
//     name; "" and "timeline" are reserved; a duplicate key throws, naming both lanes. Order: Requisition 0, Intake &
//     Panel 20, Candidates 30, registered tabs (default 50), Requisition timeline 90.
//
//   registerRequisitionBanner(def: RequisitionBannerDef): void
//     def = { key, lane, component, order? }
//     Draws `component` with { r, timeZone, can } under the StateBanner and the TA Manager notice, on every tab, in
//     `order`; it renders null when it has nothing to say. A duplicate key throws.
//
// The timeline body, the list columns and the saved views are slots too (slots.ts, C-B10-13).

import { useEffect, useMemo, type ReactNode } from "react";
import { intakeOpen, requestOpen, requisitionList, requisitionOpen, timelineRequisition, todayQueue, type RequestOpenOutput, type RequisitionFull } from "@/api/tools";
import type { AppError } from "@/api/runtime";
import { isClientRefusal, useRead } from "@/api/useTool";
import { go, useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import { V1Slot } from "@/shell/island";
import { registerView, type ViewProps } from "@/shell/viewRegistry";
import { useViewState } from "@/shell/viewState";
import { ArchivedRecord } from "@/ui/ArchivedRecord";
import { ObjectReadGuard } from "@/ui";
import { mayRecordAfterFact, RecordAfterFact } from "@/ui/RecordAfterFact";
import { RequisitionPage, RequisitionV1Controls } from "@/views/Requisitions";
import {
  Button, CARD_ROWS, CORE_LABELS, Card, CardGrid, Clocks, Count, DEFAULT_TIME_ZONE, ErrorStrip, G6_COLUMN_LABELS, G9_LABELS, GridCell, HISTORY_LABELS, LEVEL_DISPLAY,
  MenuButton, NAV_ITEM_LABELS, PIPELINE_STAGES, PageHeader, Pill, RecordCard, RecordPage, REQUISITION_STATUS, ROLE_LABELS, RULE_LABELS, SHELL_PAGE_LABELS,
  SectionTabs, Skeleton, Split, Stack, StateBanner, TextLink, formatDate, formatDateTime, useCaptureSettle,
  type KeyValueItem, type Level, type MenuItem, type PipelineStage, type RecordListRow, type StateBannerKind, type TabsItem,
} from "@/mc";
import { recordNav, useTrail } from "@/shell/recordTrail";
import { DebriefsHome } from "@/views/Debriefs";
import { useIntakePrint } from "@/views/Intake";
import { OfferTab } from "@/views/Offer";
import { PIPELINE_RULES } from "@/views/pipeline/labels";
import { COLUMN_OF } from "@/views/pipeline/pipeline";
import { PlanEditor } from "@/views/plan/PlanEditor";
import { ASSIGNMENT_WORD, COMP_WORD, CREATION_WORD, GOVERNANCE_WORD, queryOf, useSettledRead, withQuery } from "./data";
import { TIMING_LABELS } from "@/views/request/labels";
import { CompensationCard } from "./CompensationCard";
import { CreatePostCard, PostDialog } from "./CreatePost";
import { latestVersionNo, PostingStrategyCard, PostUpdateDialog, UpdateStrategyDialog } from "./PostingStrategy";
import { AssignmentCard } from "./AssignmentCard";
import { ASSIGN_LABELS, REQ_LABELS, REQ_V1_ACTIONS } from "./labels";
import { milestoneWords, RequisitionTimeline, RequisitionTrack, timelineEvents } from "./RequisitionTimeline";
import { HealthCard, ReadinessCard } from "./SpineCards";
import { requisitionBanners, requisitionTabs, type RequisitionBannerDef, type RequisitionTabDef } from "./slots";
import { StateChangeApproval, StateChangeDialog, StateChangeMenu, TaManagerNotified, bannerDetails, type StateChangeKind } from "./StateChange";

/** "" (the Requisition tab), "timeline", or the key of a tab another lane registered (registerRequisitionTab). */
export type RequisitionTab = string;

const TAB_KEY = /^[a-z][a-z0-9-]*$/;

/** Mounts another lane's tab on the requisition detail and registers its route (see the file header). */
export function registerRequisitionTab(def: RequisitionTabDef): void {
  if (!TAB_KEY.test(def.key)) throw new Error(`requisition tab: "${def.key}" is not a route segment (${def.lane})`);
  requisitionTabs.register(def);
  const tail = def.deep ? "(?:\\/.+)?" : "";
  registerView(new RegExp(`^requisitions\\/R-\\d+\\/${def.key}${tail}$`), RequisitionRoute, { lane: def.lane, specificity: 1 });
}

/** Mounts another lane's banner on the requisition detail (see the file header). */
export function registerRequisitionBanner(def: RequisitionBannerDef): void {
  requisitionBanners.register(def);
}

/** The registered route (views/b2/register.ts): `#requisitions/R-####[/<tab>[/…]]`; the query is never identity. */
export function RequisitionRoute({ route }: ViewProps) {
  const id = route.parts[0] ?? "";
  return <RequisitionDetail key={id} id={id} tab={route.parts[1] ?? ""} rest={route.parts.slice(2)} />;
}

/** The bare requisition route and its timeline tab. */
export function RequisitionDetail({ id, tab, rest = [] }: { id: string; tab: RequisitionTab; rest?: readonly string[] | undefined }) {
  const { register } = useViewState();
  const q = useRead(["requisition", id], () => requisitionOpen({ requisition_id: id }));
  useEffect(() => register({ view: "requisition", ids: { requisition: id }, summary: `requisition ${id} ${tab} ${q.data && "status" in q.data ? q.data.status : ""}` }), [register, id, tab, q.data]);
  // a refusal is the canonical not-found or no-access screen (§3.3), with nothing of the record mounted
  if (q.error && isClientRefusal(q.error)) return <ObjectReadGuard query={q} />;
  // the Interviewer's projection (P-REQ.panelist) keeps its V1 page: my rounds and the kickoff as received
  if (q.data && "projection" in q.data) return <V1Slot><RequisitionPage id={id} tab="" /></V1Slot>;
  const r = q.data && "checkpoints" in q.data ? q.data : undefined;
  return (
    <>
      {r ? <V1Controls r={r} tab={tab} /> : null}
      <Body id={id} tab={tab} rest={rest} r={r} loading={q.isPending} error={q.error} retry={() => void q.refetch()} retrying={q.isFetching} />
    </>
  );
}

/** The V1 parts above the body: the V1 controls' row (Request access, Resume, the filled close, when one shows) and the
 * dialogs the Actions menu opens from the URL — Change openings (`?action=openings`), Record what already happened
 * (`?action=record`). It reads the hash router, so it mounts with the loaded record only. */
function V1Controls({ r, tab }: { r: RequisitionFull; tab: RequisitionTab }) {
  const route = useRoute();
  const flags = queryOf(route.raw);
  const action = flags.get("action");
  const closeAction = () => go(withQuery(`#${route.raw.split("?")[0] ?? ""}`, { action: null }, flags));
  return (
    <V1Slot>
      <ArchivedRecord record={r}>
        <RequisitionV1Controls r={r} openings={{ open: action === "openings", onClose: closeAction }} />
        <RecordAfterFact id={r.id} family="requisition" section={recordSections(tab)} open={action === "record"} onClose={closeAction} />
      </ArchivedRecord>
    </V1Slot>
  );
}

/** The operations Record what already happened offers on a tab: the requisition's own, and the tab's section's where
 * its V1 page offered them (the intake's on Intake & Panel, the offer's and dispositions' on Offer). */
function recordSections(tab: RequisitionTab): string | readonly string[] {
  return tab === "intake" || tab === "offer" ? ["", tab] : "";
}

/** F-186: the header's Actions menu — each V1 control that moved here behind the gate its button had (a seat that may
 * not is never offered the item); Print the intake form on the Intake & Panel tab only. */
export function requisitionActions({ r, tab, can, viewAs, onRecord, onOpenings, onPrint }: {
  r: Pick<RequisitionFull, "can" | "state_archive">; tab: RequisitionTab; can: (tool: string) => boolean; viewAs: unknown;
  onRecord: () => void; onOpenings: () => void; onPrint: () => void;
}): MenuItem[] {
  return [
    { label: REQ_V1_ACTIONS.printIntake, icon: "file", onSelect: onPrint, can: tab === "intake" && can("intake.print"), hs: "req.print-intake" },
    { label: REQ_V1_ACTIONS.recordAfterFact, onSelect: onRecord, can: mayRecordAfterFact(can, "requisition", viewAs, r.state_archive === "archived"), hs: "req.record-after-fact" },
    { label: REQ_V1_ACTIONS.changeOpenings, onSelect: onOpenings, can: !!r.can?.["propose_openings"], hs: "req.change-openings" },
    { label: REQ_V1_ACTIONS.reviewOpeningsChanges, href: "#proposals", can: !!r.can?.["change_openings"], hs: "req.review-openings" },
  ];
}

/** Holds the capture settle signal while a read is in flight (the read itself lives above the `.mc` root). */
function useSettleWhile(busy: boolean) {
  const settle = useCaptureSettle();
  useEffect(() => {
    if (!busy) return undefined;
    let done: () => void = () => undefined;
    settle.pending(new Promise<void>((resolve) => { done = resolve; }));
    return () => done();
  }, [busy, settle]);
}

function Body({ id, tab, rest, r, loading, error, retry, retrying }: {
  id: string; tab: RequisitionTab; rest: readonly string[]; r: RequisitionFull | undefined; loading: boolean; error: AppError | null; retry: () => void; retrying: boolean;
}) {
  const { me, can } = useSession();
  useSettleWhile(loading || retrying);
  const timeZone = me?.timezone || DEFAULT_TIME_ZONE;
  if (error) {
    return (
      <Stack gap={16}>
        <PageHeader title={id} />
        <ErrorStrip
          message={error.remedy || RULE_LABELS.failedSyncRetryable}
          problem={{ status: error.status, refusal_code: error.code, detail: error.message, request_id: error.requestId }}
          onRetry={retry}
          retrying={retrying}
          live
          
        />
      </Stack>
    );
  }
  if (!r) {
    return (
      <Stack gap={16}>
        <PageHeader title={id} loading />
        <Skeleton height={64} />
        <Split main={<Skeleton lines={6} />} side={<Skeleton lines={8} />} />
      </Stack>
    );
  }
  return <Loaded r={r} tab={tab} rest={rest} timeZone={timeZone} can={can} meId={me?.person_id ?? null} />;
}

/** The administrative state the banner draws, if any (B1 Q-M2: a hold request has no word, so no banner). */
function bannerOf(r: RequisitionFull): StateBannerKind | null {
  // a waiting cancellation or reopening is the requisition's state until the HR Business Partner decides it
  if (r.pending_state_change?.kind === "cancel") return "cancellationPending";
  if (r.pending_state_change?.kind === "reopen" || r.pending_reopen) return "reopenPending";
  if (r.state_archive === "archived") return "archived";
  if (r.status === "on_hold") return "onHold";
  if (r.status === "closed_cancelled") return "cancelled";
  if (r.milestones?.governance?.key === "reopened") return "reopened";
  return null;
}

function governanceWord(r: RequisitionFull): string | null {
  if (r.pending_state_change?.kind === "cancel") return REQUISITION_STATUS.cancellationPending;
  if (r.pending_state_change?.kind === "reopen" || r.pending_reopen) return REQUISITION_STATUS.reopenPending;
  if (r.state_archive === "archived") return REQUISITION_STATUS.archived;
  return GOVERNANCE_WORD[r.status] ?? null;
}

/** The request a requisition was converted from (F-143, DC-234): the fallback for its target start, urgency and
 * position number (requisition.open carries its own copies since the B13 build pass, so a seat that may not open the
 * request still reads them), read where the seat may open it — the same read and cache the Requisition timeline uses. */
function useFromRequest(r: RequisitionFull, can: (tool: string) => boolean): RequestOpenOutput | undefined {
  const linked = /^RQ-\d+$/.test(r.request_id ?? "");
  return useSettledRead(["request", r.request_id], () => requestOpen({ request_id: r.request_id }), { enabled: linked && can("request.open"), retry: false }).data;
}

/** A date-only value ("2026-11-02") as a date: read in UTC, or it reads a day early in US zones. */
export const dateOnly = (value: unknown): string | null => (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDate(`${value}T00:00:00Z`, "UTC") : null);

function Loaded({ r, tab, rest, timeZone, can, meId }: { r: RequisitionFull; tab: RequisitionTab; rest: readonly string[]; timeZone: string; can: (tool: string) => boolean; meId: string | null }) {
  // every dialog a frame shows opens from the URL (`?action=post`, `?action=post-update`, `?action=strategy`, `?action=state-change&kind=…`)
  const route = useRoute();
  const flags = queryOf(route.raw);
  const action = flags.get("action");
  const here = route.raw.split("?")[0] ?? "";
  const openAction = (name: string | null) => go(withQuery(`#${here}`, { action: name, kind: null }, flags));
  const ask = (kind: StateChangeKind) => go(withQuery(`#${here}`, { action: "state-change", kind }, flags));
  const creation = r.milestones?.creation?.key ? CREATION_WORD[r.milestones.creation.key] : r.posted_at ? REQUISITION_STATUS.posted : REQUISITION_STATUS.created;
  const governance = governanceWord(r);
  const assignment = ASSIGNMENT_WORD[r.assignment_status];
  const level = (r.level in LEVEL_DISPLAY ? r.level : "manager_and_below") as Level;
  const banner = bannerOf(r);
  const since = banner === "archived" ? r.archived_at ?? undefined : r.milestones?.governance?.at;
  const recruiter = r.recruiter_name ?? assignment ?? "—";
  const base = `#requisitions/${r.id}`;
  const registered = requisitionTabs.list().filter((def) => def.visible?.(can, r) ?? true);
  const mounted = registered.find((def) => def.key === tab);
  const tabs = tabsOf(base, can, registered);
  const request = useFromRequest(r, can);
  const targetStart = dateOnly(r.target_start) ?? dateOnly(request?.target_start);
  const urgent = (r.urgency ?? request?.urgency) === "urgent";
  const nav = useRequisitionNav(base, tab, can);
  const { me } = useSession();
  const intakePrint = useIntakePrint(r.id);
  const actions = requisitionActions({
    r, tab: mounted?.key === "intake" ? "intake" : tab === "intake" ? "" : tab, can, viewAs: me?.view_as,
    onRecord: () => openAction("record"), onOpenings: () => openAction("openings"), onPrint: intakePrint.run,
  });
  const actionsMenu = actions.some((item) => item.can !== false)
    ? <MenuButton label={G9_LABELS.actions} items={actions} align="end"  />
    : null;
  const meta: ReactNode[] = [
    r.id,
    ...(r.req_no ? [`${REQ_LABELS.reqId} ${r.req_no}`] : []),
    LEVEL_DISPLAY[level],
    `${ROLE_LABELS.hmAbbr} ${r.hiring_manager_name}`,
    `${ROLE_LABELS.recruiter} ${recruiter}`,
    ...(r.coordinator_name ? [`${ROLE_LABELS.coordinator} ${r.coordinator_name}`] : []),
    `${ROLE_LABELS.hrbpAbbr} ${r.hrbp_name}`,
    // F-148 (DC-234): the Hiring Request this requisition was converted from, a link back
    ...(/^RQ-\d+$/.test(r.request_id ?? "") ? [<TextLink key="from-request" href={`#requests/${r.request_id}`} >{`${ASSIGN_LABELS.fromHiringRequest} ${r.request_id}`}</TextLink>] : []),
    // F-143 (DC-234): the target start date in the meta
    ...(targetStart ? [`${TIMING_LABELS.targetStartDate} ${targetStart}`] : []),
  ];
  const assignmentCard = (
    <AssignmentCard
      r={r}
      mayRequest={can("recruiter.request_reassignment") && (meId === r.recruiter_id || meId === r.hrbp_id)}
      mayAssign={can("recruiter.queue")}
      open={action === "reassign"}
      onOpen={() => openAction("reassign")}
      onClose={() => openAction(null)}
      mayReassign={can("recruiter.assign")}
      reassignOpen={action === "reassign-recruiter"}
      onReassign={() => openAction("reassign-recruiter")}
      onReassignClose={() => openAction(null)}
    />
  );
  // F-185: one page grammar — the record's way around and header, its banners, its section tabs, then a card grid
  return (
    <RecordPage
      section={nav.section}
      record={r.id}
      crumb={false}
      neighbours={nav.neighbours}
      hs={{ back: "req.back", previous: "req.previous", next: "req.next" }}
      title={r.title}
      pills={<>
        {creation ? <Pill >{creation}</Pill> : null}
        {governance ? <Pill>{governance}</Pill> : null}
        {assignment ? <Pill>{assignment}</Pill> : null}
        {urgent ? <Pill tone="flag" >{TIMING_LABELS.urgent}</Pill> : null}
      </>}
      meta={meta}
      more={<><StateChangeMenu r={r} onAsk={ask} />{actionsMenu}</>}
      action={!r.posted_at && r.can.post ? <Button tone="primary" onClick={() => openAction("post")} >{REQ_LABELS.post}</Button> : undefined}
      banners={<>
        {banner ? (
          <StateBanner
            kind={banner}
            since={since}
            timeZone={timeZone}
            daysOnHold={banner === "onHold" ? holdDays(r) : undefined}
            {...bannerDetails(r, timeZone, () => ask("reopen"))}
            
          />
        ) : null}
        {intakePrint.error ? (
          <ErrorStrip
            message={intakePrint.error.remedy || RULE_LABELS.failedSyncRetryable}
            problem={{ status: intakePrint.error.status, refusal_code: intakePrint.error.code, detail: intakePrint.error.message, request_id: intakePrint.error.requestId }}
            onRetry={intakePrint.run}
            retrying={intakePrint.busy}
            live
            
          />
        ) : null}
        <TaManagerNotified r={r} timeZone={timeZone} />
        {requisitionBanners.list().map(({ key, component: Mounted }) => <Mounted key={key} r={r} timeZone={timeZone} can={can} />)}
      </>}
      tabs={<SectionTabs label={REQ_LABELS.requisition} items={tabs} active={tab} />}
    >
      {mounted
        ? <CardGrid><GridCell span="all"><mounted.component r={r} timeZone={timeZone} rest={rest} /></GridCell></CardGrid>
        : tab === "timeline" && can("timeline.requisition")
        ? <CardGrid><GridCell span="all"><RequisitionTimeline r={r} timeZone={timeZone} auditHref={can("audit.query") ? `${base}/activity` : undefined} /></GridCell></CardGrid>
        : tab === "plan"
        ? <CardGrid><GridCell span="all"><PlanEditor id={r.id} refused={(query) => <V1Slot><ObjectReadGuard query={query} /></V1Slot>} /></GridCell></CardGrid>
        : tab === "debriefs"
        ? <DebriefsHome rid={r.id} hosted />
        : tab === "offer"
        ? <OfferTab rid={r.id} hosted />
        : (
          <Overview
            r={r}
            level={level}
            can={can}
            base={base}
            timeZone={timeZone}
            creation={creation}
            governance={governance}
            assignment={assignment}
            positionNo={r.position_no ?? request?.position_no ?? null}
            onPostUpdate={() => openAction("post-update")}
            onStrategy={() => openAction("strategy")}
            assignmentCard={assignmentCard}
            worksheetHref={can("comp_review.queue") ? `#compensation/${r.request_id}` : undefined}
          />
        )}
      {r.can.post && !r.posted_at ? <PostDialog r={r} open={action === "post"} onClose={() => openAction(null)} canReadiness={can("readiness.open")} /> : null}
      {r.can.update_strategy ? <UpdateStrategyDialog key={`strategy-${latestVersionNo(r)}`} r={r} open={action === "strategy"} onClose={() => openAction(null)} /> : null}
      {r.can.post_update ? <PostUpdateDialog key={r.row_version} r={r} open={action === "post-update"} onClose={() => openAction(null)} /> : null}
      <StateChangeDialog r={r} kind={flags.get("kind")} open={action === "state-change"} onClose={() => openAction(null)} />
      {intakePrint.dialog}
    </RecordPage>
  );
}

/** Back to the list the requisition was opened from, and its neighbours there (F-178); else the Requisitions list in
 * id order. A neighbour opens on the same tab. */
function useRequisitionNav(base: string, tab: string, can: (tool: string) => boolean) {
  const trail = useTrail();
  const list = useSettledRead(["requisitions", "ids"], () => requisitionList({ page_size: 100 }), { enabled: can("requisition.list"), retry: false });
  const ordered = useMemo(() => (list.data?.items ?? []).map((item) => item.id).sort().map((id) => `#requisitions/${id}`), [list.data]);
  const nav = recordNav(trail, base, (href) => /^#requisitions\/R-\d+$/.test(href), { label: NAV_ITEM_LABELS.requisitions, href: "#requisitions" }, ordered);
  const onTab = (href: string | null) => (href && tab ? `${href}/${tab}` : href);
  return { section: nav.section, neighbours: { previous: onTab(nav.neighbours.previous), next: onTab(nav.neighbours.next) } };
}

/** B2's tabs with the registered ones in order; a registered "intake" or "candidates" replaces that V1 link. F-185:
 * Overview · Intake & Panel · Plan · Candidates · Debriefs · Offer · (Closure) · Requisition timeline. */
function tabsOf(base: string, can: (tool: string) => boolean, registered: readonly RequisitionTabDef[]): TabsItem[] {
  const own: { order: number; item: TabsItem }[] = [
    { order: 0, item: { key: "", label: REQ_LABELS.overview, href: base } },
    ...(can("intake.open") ? [{ order: 20, item: { key: "intake", label: REQ_LABELS.intakePanel, href: `${base}/intake` } }] : []),
    ...(can("intake.plan_open") ? [{ order: 25, item: { key: "plan", label: REQ_LABELS.plan, href: `${base}/plan` } }] : []),
    ...(can("candidate.list") ? [{ order: 30, item: { key: "candidates", label: NAV_ITEM_LABELS.candidates, href: `${base}/candidates` } }] : []),
    ...(can("debrief.list_mine") ? [{ order: 40, item: { key: "debriefs", label: NAV_ITEM_LABELS.debriefs, href: `${base}/debriefs` } }] : []),
    ...(can("disposition.open_worklist") ? [{ order: 45, item: { key: "offer", label: SHELL_PAGE_LABELS.offer, href: `${base}/offer` } }] : []),
    ...(can("timeline.requisition") ? [{ order: 90, item: { key: "timeline", label: HISTORY_LABELS.requisitionTimeline, href: `${base}/timeline` } }] : []),
  ];
  const keys = new Set(registered.map((def) => def.key));
  const theirs = registered.map((def) => ({ order: def.order ?? 50, item: { key: def.key, label: def.label, href: `${base}/${def.key}` } }));
  return [...own.filter(({ item }) => !keys.has(item.key)), ...theirs]
    .map((tab, at) => ({ ...tab, at }))
    .sort((a, b) => a.order - b.order || a.at - b.at)
    .map(({ item }) => item);
}

function holdDays(r: RequisitionFull): number | undefined {
  return (r.time_clocks ?? []).find((clock) => clock.clock_key === "time_to_acceptance")?.hold_days ?? undefined;
}

/** The Overview tab (F-185): what it is and where it stands first — Status, Next action, People — then its lists —
 * Candidates, Interviews, the timeline — then the record's working cards: the waiting hold, cancel or reopen request
 * (*HRBP approval*), the Coordinator's create/post card until the posting, the recruiter assignment, the Compensation
 * card, the posting strategy, the record's fields, its clocks, its health and Search Readiness. */
function Overview({ r, level, can, base, timeZone, creation, governance, assignment, positionNo, onPostUpdate, onStrategy, assignmentCard, worksheetHref }: {
  r: RequisitionFull; level: Level; can: (tool: string) => boolean; base: string; timeZone: string; creation: string | null | undefined;
  governance: string | null; assignment: string | null | undefined; positionNo: string | null; onPostUpdate: () => void; onStrategy: () => void;
  assignmentCard: ReactNode; worksheetHref?: string | undefined;
}) {
  const strategy = r.posting_strategy?.current?.strategy_label ?? null;
  const status: KeyValueItem[] = [
    ...(governance ? ([[REQ_LABELS.status, governance]] as KeyValueItem[]) : []),
    [REQ_LABELS.compensationReview, r.compensation_review_status ? COMP_WORD[r.compensation_review_status] ?? "—" : "—"],
    [REQ_LABELS.postingStrategy, strategy ?? "—"],
    [REQ_LABELS.openings, String(r.openings_approved)],
  ];
  const people: KeyValueItem[] = [
    [ROLE_LABELS.hm, r.hiring_manager_name],
    [ROLE_LABELS.recruiter, r.recruiter_name ?? ASSIGNMENT_WORD[r.assignment_status] ?? "—"],
    [ROLE_LABELS.coordinator, r.coordinator_name ?? "—"],
    [ROLE_LABELS.hrbp, r.hrbp_name],
  ];
  return (
    <CardGrid>
      <RecordCard
        variant="summary"
        span={2}
        icon="flag"
        title={REQ_LABELS.status}
        pills={<>{creation ? <Pill>{creation}</Pill> : null}{assignment ? <Pill>{assignment}</Pill> : null}</>}
        rows={status}
        at={formatDateTime(r.updated_at, timeZone)}
        
      >
        <RequisitionTrack r={r} timeZone={timeZone} />
      </RecordCard>
      <NextAction requisitionId={r.id} />
      <RecordCard variant="summary" icon="users" title={REQ_LABELS.people} rows={people}  />
      <CandidatesCard r={r} base={base} can={can} />
      <InterviewsCard r={r} base={base} can={can} />
      <TimelineCard r={r} base={base} can={can} timeZone={timeZone} />
      <StateChangeApproval r={r} timeZone={timeZone} />
      {!r.posted_at ? <CreatePostCard r={r} timeZone={timeZone} /> : null}
      {assignmentCard}
      {/* the hiring range rows carry long labels and figures: two columns, the whole row in a narrow pane */}
      <GridCell span={2}><CompensationCard r={r} worksheetHref={worksheetHref} /></GridCell>
      <PostingStrategyCard r={r} onUpdate={onPostUpdate} onChange={onStrategy} />
      <Details r={r} positionNo={positionNo} />
      <ClocksCard r={r} level={level} />
      <HealthCard requisitionId={r.id} enabled={can("health.open")} />
      <ReadinessCard requisitionId={r.id} enabled={can("readiness.open")} />
    </CardGrid>
  );
}

/** The record's own fields (the former rail's properties). */
function Details({ r, positionNo = null }: { r: RequisitionFull; positionNo?: string | null }) {
  const items: KeyValueItem[] = [
    [REQ_LABELS.department, r.department],
    [REQ_LABELS.location, r.locations.join("; ") || "—"],
    [REQ_LABELS.openings, String(r.openings_approved)],
    // F-143 (DC-234): the request's position number among the requisition's fields, when it has one
    ...(positionNo ? ([[TIMING_LABELS.positionNumber, positionNo]] as KeyValueItem[]) : []),
  ];
  return <RecordCard variant="summary" icon="file" title={REQ_LABELS.requisition} rows={items}  />;
}

/** Calendar Time, Active Time and Days on Hold, and the targets (row 84). */
function ClocksCard({ r, level }: { r: RequisitionFull; level: Level }) {
  const acceptance = (r.time_clocks ?? []).find((clock) => clock.clock_key === "time_to_acceptance");
  const fill = (r.time_clocks ?? []).find((clock) => clock.clock_key === "time_to_fill");
  return (
    <Card tight>
      <Clocks
        calendar={acceptance?.calendar_days ?? 0}
        active={acceptance?.active_days ?? 0}
        hold={acceptance?.hold_days ?? 0}
        acceptance={acceptance?.stopped_at ? acceptance.active_days : null}
        fill={fill?.available && fill.stopped_at ? fill.active_days : null}
        level={level}
        target={acceptance?.target_days ?? undefined}
      />
    </Card>
  );
}

/** Candidates by stage (the board's seven stages, DC-31), from the requisition's own counts. */
function CandidatesCard({ r, base, can }: { r: RequisitionFull; base: string; can: (tool: string) => boolean }) {
  if (!can("candidate.list")) return null;
  const by = new Map<PipelineStage, number>();
  for (const [stage, n] of Object.entries(r.pipeline_counts ?? {})) {
    const column = COLUMN_OF[stage];
    if (column && n) by.set(column, (by.get(column) ?? 0) + n);
  }
  const total = [...by.values()].reduce((sum, n) => sum + n, 0);
  const rows: RecordListRow[] = PIPELINE_STAGES.filter((stage) => by.has(stage)).map((stage) => ({
    key: stage,
    primary: <TextLink href={`${base}/candidates`}>{G6_COLUMN_LABELS[stage]}</TextLink>,
    trailing: <Count n={by.get(stage) ?? 0} />,
  }));
  return (
    <RecordCard
      variant="list"
      icon="users"
      title={NAV_ITEM_LABELS.candidates}
      count={total}
      rows={rows}
      empty={rows.length ? undefined : PIPELINE_RULES.slate}
      all={{ href: `${base}/candidates`, what: NAV_ITEM_LABELS.candidates, always: true }}
      
    />
  );
}

/** The interview plan's rounds: each round, its interviewer and its backup. */
function InterviewsCard({ r, base, can }: { r: RequisitionFull; base: string; can: (tool: string) => boolean }) {
  const reads = can("intake.open") && r.can?.["intake"] !== false;
  const q = useSettledRead(["intake", r.id], () => intakeOpen({ requisition_id: r.id }), { enabled: reads, retry: false });
  if (!reads || (q.error && isClientRefusal(q.error))) return null;
  const rounds = (q.data?.rounds ?? []).filter((round) => round.in_use !== false).sort((a, b) => a.sequence - b.sequence);
  const nameOf = (person: Record<string, unknown> | null) => (person && typeof person["name"] === "string" ? person["name"] : null);
  const rows: RecordListRow[] = rounds.map((round) => ({
    key: round.id,
    primary: round.label,
    secondary: [nameOf(round.primary), nameOf(round.backup)].filter(Boolean).join(" · ") || "—",
  }));
  const planHref = can("intake.plan_open") ? `${base}/plan` : `${base}/intake`;
  return (
    <RecordCard
      variant="list"
      icon="calendar"
      title={NAV_ITEM_LABELS.interviews}
      count={q.data ? rows.length : undefined}
      loading={q.isPending ? { lines: 3 } : undefined}
      rows={rows}
      empty={q.data && !rows.length ? CORE_LABELS.nothingNeedsAction : undefined}
      all={{ href: planHref, what: NAV_ITEM_LABELS.interviews, always: true }}
      
    />
  );
}

/** The newest events of the requisition timeline, with their actor and time. */
function TimelineCard({ r, base, can, timeZone }: { r: RequisitionFull; base: string; can: (tool: string) => boolean; timeZone: string }) {
  const reads = can("timeline.requisition");
  const q = useSettledRead(["timeline", r.id], () => timelineRequisition({ requisition_id: r.id, limit: 500 }), { enabled: reads });
  if (!reads) return null;
  const events = q.data ? timelineEvents(q.data.rows, milestoneWords(r)) : [];
  const rows: RecordListRow[] = events.map((event) => ({
    key: event.id,
    primary: event.sentence,
    secondary: [event.actor?.name, formatDateTime(event.at, timeZone)].filter(Boolean).join(" · "),
  }));
  return (
    <RecordCard
      variant="list"
      icon="clock"
      title={HISTORY_LABELS.requisitionTimeline}
      loading={q.isPending ? { lines: 3 } : undefined}
      rows={rows}
      empty={q.data && !rows.length ? CORE_LABELS.nothingNeedsAction : undefined}
      all={{ href: `${base}/timeline`, what: HISTORY_LABELS.requisitionTimeline, always: true }}
      
    />
  );
}

/** "show current state, next action, blockers …": the open rows addressed to the viewer on this requisition. */
function NextAction({ requisitionId }: { requisitionId: string }) {
  const q = useRead(["today", "needs_you", requisitionId], () => todayQueue({ requisition_id: requisitionId, section: "needs_you" }));
  useSettleWhile(q.isPending);
  const rows = useMemo(() => (q.data ? [...q.data.needs_you, ...q.data.overdue].filter((row, index, all) => all.findIndex((other) => other.id === row.id) === index) : []), [q.data]);
  return (
    <RecordCard
      variant="list"
      icon="target"
      title={REQ_LABELS.nextAction}
      count={q.data ? rows.length : undefined}
      loading={q.isPending ? { lines: 2 } : undefined}
      error={q.error ? { message: q.error.remedy || RULE_LABELS.failedSyncRetryable, onRetry: () => void q.refetch() } : undefined}
      empty={q.data && !rows.length ? CORE_LABELS.nothingNeedsAction : undefined}
      rows={rows.map((row, index) => ({
        key: row.id,
        primary: row.route ? <TextLink href={row.route} >{row.title}</TextLink> : row.title,
      }))}
      all={rows.length > CARD_ROWS ? { href: "#action-center", what: NAV_ITEM_LABELS.actionCenter } : undefined}
    />
  );
}
